/*
 * Recently Viewed Products
 * ------------------------------------------------------------
 * Tracks the product handles a customer has viewed in
 * localStorage and renders them inside any
 * <recently-viewed-products> element on the page using the
 * Shopify Section Rendering API.
 */

(function () {
  if (window.customElements.get('recently-viewed-products')) return;

  const DEFAULT_KEY = 'mysticparcel:recently-viewed';
  const MAX_HISTORY = 24;

  function readHistory(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  function writeHistory(key, handles) {
    try {
      localStorage.setItem(key, JSON.stringify(handles.slice(0, MAX_HISTORY)));
    } catch (e) {
      /* storage full or unavailable — ignore */
    }
  }

  function trackCurrent(key, handle) {
    if (!handle) return;
    const history = readHistory(key);
    const next = [handle, ...history.filter((h) => h !== handle)];
    writeHistory(key, next);
  }

  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();
      this.storageKey = this.dataset.storageKey || DEFAULT_KEY;
      this.currentHandle = this.dataset.productHandle || '';
      this.sectionId = this.dataset.sectionId || '';
      this.maxItems = parseInt(this.dataset.productsToShow, 10) || 4;
    }

    connectedCallback() {
      // Record this product in the customer's view history.
      trackCurrent(this.storageKey, this.currentHandle);

      // Build the visible list from history (excluding the current product).
      const handles = readHistory(this.storageKey)
        .filter((h) => h && h !== this.currentHandle)
        .slice(0, this.maxItems);

      if (handles.length === 0) {
        this.remove();
        return;
      }

      this.render(handles);
    }

    async render(handles) {
      const grid = this.querySelector('.recently-viewed__grid');
      if (!grid) {
        this.remove();
        return;
      }

      // Reset the placeholder card that Liquid rendered for the host product.
      grid.innerHTML = '';

      const fetched = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
      const cards = fetched.filter(Boolean);

      if (cards.length === 0) {
        this.remove();
        return;
      }

      cards.forEach((card) => grid.appendChild(card));
      this.hidden = false;
    }

    async fetchCard(handle) {
      try {
        const url = `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.sectionId)}`;
        const response = await fetch(url);
        if (!response.ok) return null;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // The placeholder item is tagged with the product handle so we can
        // grab exactly the card we asked for.
        return (
          doc.querySelector(`.recently-viewed__item[data-product-handle="${handle}"]`) ||
          doc.querySelector('.recently-viewed__item')
        );
      } catch (e) {
        return null;
      }
    }
  }

  window.customElements.define('recently-viewed-products', RecentlyViewedProducts);
})();
